/**
 * /settings — tenant-scoped settings.
 *
 * Phase 3.6.B introduces this route as the new home for everything that
 * isn't project-scoped: user management, clients, role management,
 * teams, my profile, my organization. Project-scoped settings (members,
 * labels, statuses, danger zone) keep living at
 * `/projects/:projectId/settings` and are reached from the board
 * header's gear icon.
 *
 * Tabs are query-driven (`?tab=clients`) rather than nested routes so
 * the user can deep-link to any tab and the back button restores the
 * previous tab cleanly. Default tab is `user-management` when no
 * `?tab=` is set, falling back to the first tab the caller can see.
 *
 * Auth gate: bounce to /login if the user has no token. Tab visibility
 * comes from `useTenantPermissions()` — the management tabs render only
 * for a caller holding the slug the BE gates their writes on, so nobody
 * is shown a surface where every action 403s.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { getAccessToken } from '@/auth/cookie-helpers'
import { TabBar, TabButton } from '@/components/TabButton'
import { IntegrationsPanel } from '@/features/code/IntegrationsPanel'
import { useMe } from '@/features/me/use-me'
import { useTenantPermissions } from '@/features/me/use-tenant-permissions'
import { NotificationsSettingsPanel } from '@/features/notifications/components/NotificationsSettingsPanel'
import { ClientsPanel } from '@/features/tenant-settings/ClientsPanel'
import { DevPanel } from '@/features/tenant-settings/DevPanel'
import { GuestsPanel } from '@/features/tenant-settings/GuestsPanel'
import { MyOrganizationPanel } from '@/features/tenant-settings/MyOrganizationPanel'
import { MyProfilePanel } from '@/features/tenant-settings/MyProfilePanel'
import { RoleManagementPanel } from '@/features/tenant-settings/RoleManagementPanel'
import { TeamsPanel } from '@/features/tenant-settings/TeamsPanel'
import { UserManagementPanel } from '@/features/tenant-settings/UserManagementPanel'

const TAB_VALUES = [
  'user-management',
  'clients',
  'roles',
  'teams',
  'my-profile',
  'notifications',
  'my-organization',
  'guests',
  'integrations',
  'dev',
] as const
type TabValue = (typeof TAB_VALUES)[number]

/**
 * Whether the Dev tab is visible. Staging-only — never on production.
 * Same gate the previous in-sidebar toggle used; consolidated here so
 * the visibility check lives next to the tab definition.
 */
function isDevHost(): boolean {
  return typeof window !== 'undefined' && window.location.host.includes('-staging')
}

interface SearchParams {
  tab?: TabValue
  /**
   * Integrations tab: the connection whose detail sheet is open
   * (deep-linkable), and the return leg of "Connect GitHub" — the API
   * redirects here with `github=finish` (the one-time code rides in the URL
   * fragment) or `error=<reason>` when GitHub reported a cancel.
   */
  connection?: string
  github?: 'finish'
  error?: string
}

export const Route = createFileRoute('/settings/')({
  component: SettingsRoute,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const tab = search.tab
    if (typeof tab !== 'string' || !(TAB_VALUES as readonly string[]).includes(tab)) return {}
    const out: SearchParams = { tab: tab as TabValue }
    if (tab === 'integrations') {
      if (typeof search.connection === 'string' && search.connection.length > 0) {
        out.connection = search.connection
      }
      if (search.github === 'finish') out.github = 'finish'
      if (typeof search.error === 'string' && search.error.length > 0) out.error = search.error
    }
    return out
  },
})

function SettingsRoute() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return <SettingsPage />
}

function SettingsPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/settings/' })
  const perms = useTenantPermissions()
  // `useTenantPermissions` falls OPEN on an absent `permissions` array so
  // an older BE can't lock anyone out — but "still in flight" and "the BE
  // doesn't serialize the field" are different conditions, and only the
  // second one deserves fall-open. Without this gate a hard load of
  // `/settings?tab=roles` mounts the roles panel for the frame before
  // `/api/me` lands, which fires a request that 403s for anyone below
  // owner. `useMe` is already warm from the AppShell on in-app
  // navigation, so this only defers the very first paint after a refresh.
  const permsResolved = !useMe().isPending
  const { t } = useTranslation('settings')

  // Every management tab is gated on the same slug the BE checks, so a
  // role only sees a tab it can actually act on. Note the seeded grants
  // are not the ladder the role names imply: `admin` holds every slug
  // EXCEPT `manage-org` and `manage-roles` (migration `0015`), so Roles
  // and My Organization are owner-only unless a tenant regrants them.
  // My Profile and Notifications stay open to everyone — without them a
  // member would land on an empty Settings page.
  const tabDefs: { value: TabValue; label: string; visible: boolean }[] = [
    { value: 'user-management', label: t('tabs.userManagement'), visible: perms.canManageUsers },
    { value: 'clients', label: t('tabs.clients'), visible: perms.canManageClients },
    { value: 'roles', label: t('tabs.roles'), visible: perms.canManageRoles },
    { value: 'teams', label: t('tabs.teams'), visible: perms.canManageTeams },
    { value: 'my-profile', label: t('tabs.myProfile'), visible: true },
    { value: 'notifications', label: t('tabs.notifications'), visible: true },
    { value: 'my-organization', label: t('tabs.myOrganization'), visible: perms.canManageOrg },
    // Org-level, gated on the same slug, and it belongs next to the other
    // thing only an owner sees. The tab bar's order follows this array.
    { value: 'guests', label: t('tabs.guests'), visible: perms.canManageOrg },
    // Code connections. Gated on its own slug (owner + admin by seed), so
    // an admin who lacks `manage-org` still sees it.
    {
      value: 'integrations',
      label: t('tabs.integrations'),
      visible: perms.canManageIntegrations,
    },
    { value: 'dev', label: t('tabs.dev'), visible: isDevHost() },
  ]
  const visibleTabs = permsResolved ? tabDefs.filter((tab) => tab.visible) : []

  // Resolve the active tab against what's actually visible, and render
  // the panels off THAT — otherwise a hand-typed or bookmarked
  // `?tab=roles` would still mount RoleManagementPanel for a member, and
  // the default `user-management` would mount the users table for
  // everyone. Falling back to the first visible tab keeps the page
  // useful; `my-profile` is the last-resort floor since it is always on.
  // `null` until permissions resolve, which keeps every panel unmounted
  // for that frame rather than flashing one the caller may not be
  // allowed to see.
  const requestedTab: TabValue = search.tab ?? 'user-management'
  const activeTab: TabValue | null = !permsResolved
    ? null
    : visibleTabs.some((tab) => tab.value === requestedTab)
      ? requestedTab
      : (visibleTabs[0]?.value ?? 'my-profile')

  const goToTab = (tab: TabValue) => {
    navigate({
      to: '/settings',
      search: tab === 'user-management' ? {} : { tab },
      replace: true,
    })
  }

  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="border-b border-border bg-bg-elevated px-6 py-5">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-1">
          <h1 className="text-2xl font-semibold text-fg">{t('page.title')}</h1>
          <p className="text-sm text-fg-muted">{t('page.subtitle')}</p>
        </div>
      </header>

      <TabBar label={t('page.tabsLabel')}>
        {visibleTabs.map((tab) => (
          <TabButton
            key={tab.value}
            active={activeTab === tab.value}
            onClick={() => goToTab(tab.value)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabBar>

      <div className="flex-1 px-6 py-6">
        <div className="mx-auto w-full max-w-6xl">
          {activeTab === 'user-management' && <UserManagementPanel />}
          {activeTab === 'clients' && <ClientsPanel />}
          {activeTab === 'roles' && <RoleManagementPanel />}
          {activeTab === 'teams' && <TeamsPanel />}
          {activeTab === 'my-profile' && <MyProfilePanel />}
          {activeTab === 'notifications' && <NotificationsSettingsPanel />}
          {activeTab === 'my-organization' && (
            <MyOrganizationPanel canManage={perms.canManageOrg} />
          )}
          {activeTab === 'guests' && <GuestsPanel />}
          {activeTab === 'integrations' && <IntegrationsPanel />}
          {activeTab === 'dev' && isDevHost() && <DevPanel />}
        </div>
      </div>
    </div>
  )
}
